// [PURPOSE]: Persist tutorial completion state to Firebase (when authenticated)
//   AND localStorage (always, as cache + offline fallback).
// [WHY]: P1.4 — User report: "Tutorials seem to refresh if I log in on a new
//   private window." TutorialContext previously wrote to localStorage only,
//   so private windows / cleared caches lost progress for authenticated users.
//   Tutorial state must follow the user's account, not the device.
// [INTEGRATION]: Consumed by TutorialContext on load + every state change.
// [CREATED]: April 29, 2026 — Pass B P1.4 Tutorial Firebase sync.

import { ref, get, set } from 'firebase/database';
import { database } from '../firebase/config';
import { safeLocalStorageSet } from '../utils/storageValidator';

// ========================================
// CONSTANTS
// ========================================

export const STORAGE_KEY = 'tabletopInventory_tutorialState';
const FB_PATH_PREFIX = 'users';
const FB_PATH_SUFFIX = 'tutorialState';

// ========================================
// LOCAL STORAGE HELPERS
// ========================================

/**
 * Load tutorial state from localStorage.
 * [WHY]: Pure helper — TutorialContext keeps its own legacy migration logic
 *   (old `tutorialComplete` key) but for Firebase round-trip we only deal with
 *   the modern shape.
 * @returns {{completedModules: Object, declinedModules: Object}|null}
 *   null if the key is missing or unparseable, never throws.
 */
export function loadTutorialFromLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      completedModules: parsed.completedModules || {},
      declinedModules: parsed.declinedModules || {},
    };
  } catch (e) {
    console.warn('[tutorialPersistence] loadTutorialFromLocal failed:', e?.message);
    return null;
  }
}

/**
 * Save tutorial state to localStorage via safeLocalStorageSet (handles quota).
 * @param {{completedModules: Object, declinedModules: Object}} state
 * @returns {boolean} true on success, false on failure (logged, not thrown).
 */
export function saveTutorialToLocal(state) {
  try {
    const payload = JSON.stringify({
      completedModules: state?.completedModules || {},
      declinedModules: state?.declinedModules || {},
    });
    return safeLocalStorageSet(STORAGE_KEY, payload);
  } catch (e) {
    console.warn('[tutorialPersistence] saveTutorialToLocal failed:', e?.message);
    return false;
  }
}

// ========================================
// FIREBASE HELPERS
// ========================================

/**
 * Load tutorial state from Firebase RTDB at users/{uid}/tutorialState.
 * [WHY]: When user signs in (or app starts already authenticated), pull their
 *   account-level tutorial progress so it follows them across devices /
 *   private windows.
 * [GRACEFUL]: Returns null on any failure (no DB, no auth, network error,
 *   permission denied). Caller falls back to localStorage.
 * @param {string} uid - Firebase user uid.
 * @returns {Promise<{completedModules: Object, declinedModules: Object}|null>}
 */
export async function loadTutorialFromFirebase(uid) {
  if (!database || !uid) return null;
  try {
    const snapshot = await get(ref(database, `${FB_PATH_PREFIX}/${uid}/${FB_PATH_SUFFIX}`));
    if (!snapshot.exists()) return null;
    const value = snapshot.val();
    if (!value || typeof value !== 'object') return null;
    return {
      completedModules: value.completedModules || {},
      declinedModules: value.declinedModules || {},
    };
  } catch (e) {
    console.warn('[tutorialPersistence] loadTutorialFromFirebase failed:', e?.message);
    return null;
  }
}

/**
 * Save tutorial state to Firebase RTDB at users/{uid}/tutorialState.
 * [GRACEFUL]: Logs and resolves false on failure — never throws so that a
 *   transient network blip doesn't crash the tutorial state machine.
 * @param {string} uid
 * @param {{completedModules: Object, declinedModules: Object}} state
 * @returns {Promise<boolean>} true on success, false on failure.
 */
export async function saveTutorialToFirebase(uid, state) {
  if (!database || !uid) return false;
  try {
    await set(ref(database, `${FB_PATH_PREFIX}/${uid}/${FB_PATH_SUFFIX}`), {
      completedModules: state?.completedModules || {},
      declinedModules: state?.declinedModules || {},
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (e) {
    console.warn('[tutorialPersistence] saveTutorialToFirebase failed:', e?.message);
    return false;
  }
}

// ========================================
// MERGE LOGIC
// ========================================

/**
 * Merge two tutorial states. A module marked complete (or declined) in EITHER
 * source stays complete (or declined) in the result. Same for declined.
 * [WHY]: A user might have completed [welcome] offline (localStorage only) and
 *   [shop] on another device (Firebase only). On login we want both flagged so
 *   neither tutorial re-prompts.
 * [DESIGN DECISION]: Union (boolean OR) on each module id. Last-write-wins
 *   would erase progress; intersection would over-prompt. Union matches the
 *   user's mental model: "I already saw this tutorial somewhere."
 * @param {{completedModules?: Object, declinedModules?: Object}|null} a
 * @param {{completedModules?: Object, declinedModules?: Object}|null} b
 * @returns {{completedModules: Object, declinedModules: Object}}
 */
export function mergeTutorialState(a, b) {
  const safeA = a || {};
  const safeB = b || {};
  const completedA = safeA.completedModules || {};
  const completedB = safeB.completedModules || {};
  const declinedA = safeA.declinedModules || {};
  const declinedB = safeB.declinedModules || {};

  const mergedCompleted = {};
  const mergedDeclined = {};

  // Union of completed: any truthy value in either source wins.
  for (const key of new Set([...Object.keys(completedA), ...Object.keys(completedB)])) {
    if (completedA[key] || completedB[key]) {
      mergedCompleted[key] = true;
    }
  }
  for (const key of new Set([...Object.keys(declinedA), ...Object.keys(declinedB)])) {
    if (declinedA[key] || declinedB[key]) {
      mergedDeclined[key] = true;
    }
  }

  return {
    completedModules: mergedCompleted,
    declinedModules: mergedDeclined,
  };
}
